import type {
  GraphMember,
  Relationship,
  Side,
} from '@/contexts/FamilyGraphContext';

import {
  resolveCanonicalTreeRelationshipCreationPolicy,
} from './CanonicalTreeRelationshipCreationPolicy';

import {
  planCanonicalTreeAncestryReconnection,
} from './CanonicalTreeAncestryReconnectionAuthority';

export const
PROVISIONAL_SUBJECT_ENDPOINT_TOKEN =
  'SUBJECT' as const;

export type CanonicalTreeEndpointFactPlannerStatus =
  | 'READY_FOR_IDENTITY_RESOLUTION'
  | 'DERIVED_ONLY'
  | 'REQUIRES_ADDITIONAL_ENDPOINTS'
  | 'BLOCKED';

export type CanonicalTreeEndpointFactClass =
  | 'parent-child'
  | 'sibling'
  | 'spouse-partner'
  | 'former-spouse-partner'
  | 'co-parent'
  | 'step-family'
  | 'in-law'
  | 'guardian'
  | 'foster-family'
  | 'god-family'
  | 'chosen-family'
  | 'affiliated-family';

export type CanonicalTreeEndpointReference =
  | Readonly<{
      kind:
        'canonical-member';
      memberId: string;
    }>
  | Readonly<{
      kind:
        'provisional-subject';
      token:
        typeof PROVISIONAL_SUBJECT_ENDPOINT_TOKEN;
    }>;

export interface CanonicalTreeEndpointFactProposal {
  readonly factClass:
    CanonicalTreeEndpointFactClass;

  readonly source:
    CanonicalTreeEndpointReference;

  readonly target:
    CanonicalTreeEndpointReference;

  readonly direction:
    | 'directed'
    | 'symmetric'
    | 'child-mediated';

  readonly createsAncestry:
    boolean;

  readonly partnershipStatus:
    | 'current'
    | 'former'
    | 'coparent-only'
    | 'not-applicable';

  readonly evidenceMemberIds:
    readonly string[];
}

export interface CanonicalTreeEndpointFactPreview {
  readonly endpointLabels:
    readonly string[];

  readonly factClassLabel:
    string;

  readonly directionLabel:
    string;

  readonly ancestryEffectLabel:
    string;

  readonly derivedEffectLabel:
    string;

  readonly blockerLabels:
    readonly string[];
}

export interface CanonicalTreeEndpointFactPlannerInput {
  readonly members:
    readonly GraphMember[];

  readonly viewerMemberId:
    string | null;

  readonly relationship:
    Relationship;

  readonly optionKey:
    string;

  readonly requestedSide?:
    Side | null;

  readonly subjectMemberId?:
    string | null;

  readonly tappedMemberId?:
    string | null;

  readonly connectorMemberId?:
    string | null;

  readonly secondaryConnectorMemberId?:
    string | null;

  readonly displayLabel?:
    string | null;

  readonly relationshipOverride?:
    string | null;
}

export interface CanonicalTreeEndpointFactPlannerResult {
  readonly status:
    CanonicalTreeEndpointFactPlannerStatus;

  readonly factProposals:
    readonly CanonicalTreeEndpointFactProposal[];

  readonly requiredEvidence:
    readonly string[];

  readonly blockers:
    readonly string[];

  readonly derivedViewerEffects:
    readonly string[];

  readonly preview:
    CanonicalTreeEndpointFactPreview;

  readonly mutatesCanonicalGraph:
    false;

  readonly executionAuthorized:
    false;

  readonly writeAuthorized:
    false;

  readonly identityResolutionPerformed:
    false;

  readonly subjectIdentityResolutionRequired:
    boolean;
}

/*
 * CC-TREE-LIVE-003G3WB GREEN v1 endpoint-fact planner.
 *
 * Pure planning authority only:
 * - no identity resolution,
 * - no execution authority,
 * - no write authority,
 * - no graph mutation,
 * - unsupported or unproven authoring intent fails closed.
 */

const activeCanonicalMember = (
  member: GraphMember,
): boolean =>
  member.approvalStatus !== 'rejected' &&
  (
    member.profileState as string
  ) !== 'deleted';

const canonicalEndpoint = (
  memberId: string,
): Readonly<{
  kind: 'canonical-member';
  memberId: string;
}> =>
  Object.freeze({
    kind:
      'canonical-member' as const,
    memberId,
  });

const provisionalSubjectEndpoint =
  (): CanonicalTreeEndpointReference =>
    Object.freeze({
      kind:
        'provisional-subject' as const,
      token:
        PROVISIONAL_SUBJECT_ENDPOINT_TOKEN,
    });

const endpointKey = (
  endpoint:
    CanonicalTreeEndpointReference,
): string =>
  endpoint.kind ===
    'canonical-member'
    ? 'C:' + endpoint.memberId
    : 'P:' + endpoint.token;

const sortedUnique = (
  values: readonly string[],
): readonly string[] =>
  Object.freeze(
    Array.from(
      new Set(
        values.filter(Boolean),
      ),
    ).sort(),
  );

const proposalSortKey = (
  proposal:
    CanonicalTreeEndpointFactProposal,
): string =>
  JSON.stringify([
    proposal.factClass,
    proposal.direction,
    endpointKey(
      proposal.source,
    ),
    endpointKey(
      proposal.target,
    ),
    proposal.partnershipStatus,
    [...proposal.evidenceMemberIds].sort(),
  ]);

const freezeProposal = (
  proposal:
    CanonicalTreeEndpointFactProposal,
): CanonicalTreeEndpointFactProposal =>
  Object.freeze({
    ...proposal,
    source:
      Object.freeze({
        ...proposal.source,
      }),
    target:
      Object.freeze({
        ...proposal.target,
      }),
    evidenceMemberIds:
      sortedUnique(
        proposal.evidenceMemberIds,
      ),
  });

interface FinalizeInput {
  readonly status:
    CanonicalTreeEndpointFactPlannerStatus;
  readonly proposals?:
    readonly CanonicalTreeEndpointFactProposal[];
  readonly requiredEvidence?:
    readonly string[];
  readonly blockers?:
    readonly string[];
  readonly derivedViewerEffects?:
    readonly string[];
  readonly subjectIdentityResolutionRequired:
    boolean;
  readonly memberById:
    ReadonlyMap<string, GraphMember>;
}

const finalizePlannerResult = (
  input: FinalizeInput,
): CanonicalTreeEndpointFactPlannerResult => {
  const factProposals =
    Object.freeze(
      [...(
        input.proposals ?? []
      )]
        .map(freezeProposal)
        .sort(
          (left, right) =>
            proposalSortKey(
              left,
            ).localeCompare(
              proposalSortKey(
                right,
              ),
            ),
        ),
    );

  const requiredEvidence =
    sortedUnique(
      input.requiredEvidence ?? [],
    );

  const blockers =
    sortedUnique(
      input.blockers ?? [],
    );

  const derivedViewerEffects =
    sortedUnique(
      input.derivedViewerEffects ??
        [],
    );

  const endpointLabels =
    Object.freeze(
      factProposals.flatMap(
        proposal =>
          [
            proposal.source,
            proposal.target,
          ].map(endpoint => {
            if (
              endpoint.kind ===
              'provisional-subject'
            ) {
              return 'New family member';
            }

            return (
              input.memberById.get(
                endpoint.memberId,
              )?.name ??
              endpoint.memberId
            );
          }),
      ),
    );

  const first =
    factProposals[0] ??
    null;

  return Object.freeze({
    status:
      input.status,

    factProposals,

    requiredEvidence,

    blockers,

    derivedViewerEffects,

    preview:
      Object.freeze({
        endpointLabels,

        factClassLabel:
          first?.factClass ?? '',

        directionLabel:
          first?.direction ?? '',

        ancestryEffectLabel:
          first
            ? (
                first.createsAncestry
                  ? 'Creates canonical ancestry'
                  : 'Does not create canonical ancestry'
              )
            : '',

        derivedEffectLabel:
          derivedViewerEffects.join(
            ', ',
          ),

        blockerLabels:
          blockers,
      }),

    mutatesCanonicalGraph:
      false as const,

    executionAuthorized:
      false as const,

    writeAuthorized:
      false as const,

    identityResolutionPerformed:
      false as const,

    subjectIdentityResolutionRequired:
      input
        .subjectIdentityResolutionRequired,
  });
};

const evidenceForEndpoints = (
  endpoints:
    readonly CanonicalTreeEndpointReference[],
): readonly string[] =>
  sortedUnique(
    endpoints.flatMap(
      endpoint =>
        endpoint.kind ===
          'canonical-member'
          ? [endpoint.memberId]
          : [],
    ),
  );

const topologyBlocker = (
  status: string,
): string => {
  switch (status) {
    case 'BLOCKED_CROSS_GRAPH':
      return 'CROSS_GRAPH_ENDPOINT';

    case 'BLOCKED_SELF_PARENT':
      return 'SELF_PARENT_FACT';

    case 'BLOCKED_CYCLE':
      return 'ANCESTRY_CYCLE';

    case 'BLOCKED_MISSING_MEMBER':
    case 'BLOCKED_MISSING_SUBJECT':
    case 'BLOCKED_MISSING_ANCHOR':
      return 'STALE_CANONICAL_ENDPOINT';

    default:
      return 'TOPOLOGY_VALIDATION_FAILED';
  }
};

export const planCanonicalTreeEndpointFacts = (
  input:
    CanonicalTreeEndpointFactPlannerInput,
): CanonicalTreeEndpointFactPlannerResult => {
  /*
   * Stage 1 — creation policy.
   *
   * Presentation text and overrides never change
   * canonical creation disposition.
   */
  const policy =
    resolveCanonicalTreeRelationshipCreationPolicy(
      input.optionKey,
    );

  const activeMembers =
    input.members.filter(
      activeCanonicalMember,
    );

  const memberById =
    new Map(
      activeMembers.map(
        member =>
          [
            member.id,
            member,
          ] as const,
      ),
    );

  const provisionalSubject =
    !input.subjectMemberId;

  if (
    policy.disposition ===
      'DERIVED_ONLY'
  ) {
    return finalizePlannerResult({
      status:
        'DERIVED_ONLY',
      proposals:
        [],
      requiredEvidence:
        [],
      blockers:
        [],
      derivedViewerEffects:
        [],
      subjectIdentityResolutionRequired:
        provisionalSubject,
      memberById,
    });
  }

  /*
   * Stage 2 — exact viewer authority.
   */
  const viewerId =
    input.viewerMemberId;

  if (!viewerId) {
    return finalizePlannerResult({
      status:
        'BLOCKED',
      blockers:
        [
          'MISSING_CANONICAL_VIEWER',
        ],
      subjectIdentityResolutionRequired:
        provisionalSubject,
      memberById,
    });
  }

  const viewer =
    memberById.get(
      viewerId,
    );

  if (!viewer) {
    return finalizePlannerResult({
      status:
        'BLOCKED',
      blockers:
        [
          'MISSING_CANONICAL_VIEWER',
        ],
      subjectIdentityResolutionRequired:
        provisionalSubject,
      memberById,
    });
  }

  const graphOwnerUserId =
    viewer.ownerUserId;

  /*
   * Stage 3 — subject reference.
   */
  let subject:
    GraphMember | null =
      null;

  let subjectReference:
    CanonicalTreeEndpointReference;

  if (
    input.subjectMemberId
  ) {
    subject =
      memberById.get(
        input.subjectMemberId,
      ) ??
      null;

    if (!subject) {
      return finalizePlannerResult({
        status:
          'BLOCKED',
        blockers:
          [
            'STALE_SUBJECT_ENDPOINT',
          ],
        subjectIdentityResolutionRequired:
          false,
        memberById,
      });
    }

    if (
      subject.ownerUserId !==
      graphOwnerUserId
    ) {
      return finalizePlannerResult({
        status:
          'BLOCKED',
        blockers:
          [
            'CROSS_GRAPH_ENDPOINT',
          ],
        subjectIdentityResolutionRequired:
          false,
        memberById,
      });
    }

    subjectReference =
      canonicalEndpoint(
        subject.id,
      );
  } else {
    subjectReference =
      provisionalSubjectEndpoint();
  }

  const subjectIdentityResolutionRequired =
    subjectReference.kind ===
      'provisional-subject';

  /*
   * Stage 4 — endpoint resolution.
   *
   * A supplied higher-precedence endpoint that is stale
   * or cross-graph blocks; it never falls back.
   */
  const resolveEndpoint = (
    ids:
      readonly (
        | string
        | null
        | undefined
      )[],
    allowViewerDefault:
      boolean,
  ):
    | Readonly<{
        ok: true;
        member:
          GraphMember;
      }>
    | Readonly<{
        ok: false;
        missing: boolean;
        blocker:
          string;
      }> => {
    const selected =
      ids.find(
        value =>
          typeof value ===
            'string' &&
          value.length > 0,
      ) ??
      (
        allowViewerDefault
          ? viewer.id
          : null
      );

    if (!selected) {
      return Object.freeze({
        ok:
          false as const,
        missing:
          true,
        blocker:
          'REQUIRED_ENDPOINT_MISSING',
      });
    }

    const member =
      memberById.get(
        selected,
      );

    if (!member) {
      return Object.freeze({
        ok:
          false as const,
        missing:
          false,
        blocker:
          'STALE_CANONICAL_ENDPOINT',
      });
    }

    if (
      member.ownerUserId !==
      graphOwnerUserId
    ) {
      return Object.freeze({
        ok:
          false as const,
        missing:
          false,
        blocker:
          'CROSS_GRAPH_ENDPOINT',
      });
    }

    return Object.freeze({
      ok:
        true as const,
      member,
    });
  };

  const blockedEndpointResult = (
    blocker:
      string,
  ): CanonicalTreeEndpointFactPlannerResult =>
    finalizePlannerResult({
      status:
        'BLOCKED',
      blockers:
        [blocker],
      subjectIdentityResolutionRequired,
      memberById,
    });

  const missingEndpointResult = (
    blocker:
      string,
    evidence:
      readonly string[] = [],
  ): CanonicalTreeEndpointFactPlannerResult =>
    finalizePlannerResult({
      status:
        'REQUIRES_ADDITIONAL_ENDPOINTS',
      requiredEvidence:
        evidence,
      blockers:
        [blocker],
      subjectIdentityResolutionRequired,
      memberById,
    });

  const ready = (
    proposals:
      readonly CanonicalTreeEndpointFactProposal[],
    derivedViewerEffects:
      readonly string[] = [],
  ): CanonicalTreeEndpointFactPlannerResult =>
    finalizePlannerResult({
      status:
        'READY_FOR_IDENTITY_RESOLUTION',
      proposals,
      derivedViewerEffects,
      subjectIdentityResolutionRequired,
      memberById,
    });

  const validateParentShape = (
    parent:
      CanonicalTreeEndpointReference,
    child:
      CanonicalTreeEndpointReference,
  ): string | null => {
    if (
      parent.kind !==
        'canonical-member' ||
      child.kind !==
        'canonical-member'
    ) {
      /*
       * A provisional new identity cannot already be
       * an ancestor or descendant in the canonical
       * graph. Existing-member topology is validated
       * below by the canonical reconnection authority.
       */
      return null;
    }

    const result =
      planCanonicalTreeAncestryReconnection({
        members:
          activeMembers,

        subjectMemberId:
          parent.memberId,

        intent:
          'ADD_SUBJECT_AS_PARENT',

        anchorMemberId:
          child.memberId,
      });

    return result.authorized
      ? null
      : topologyBlocker(
          result.status,
        );
  };

  /*
   * Stage 5 — exact normalization authority.
   */
  if (
    input.optionKey ===
      'mother' ||
    input.optionKey ===
      'father'
  ) {
    const blocker =
      validateParentShape(
        subjectReference,
        canonicalEndpoint(
          viewer.id,
        ),
      );

    if (blocker) {
      return blockedEndpointResult(
        blocker,
      );
    }

    return ready([
      {
        factClass:
          'parent-child',

        source:
          subjectReference,

        target:
          canonicalEndpoint(
            viewer.id,
          ),

        direction:
          'directed',

        createsAncestry:
          true,

        partnershipStatus:
          'not-applicable',

        evidenceMemberIds:
          evidenceForEndpoints([
            subjectReference,
            canonicalEndpoint(
              viewer.id,
            ),
          ]),
      },
    ]);
  }

  if (
    input.optionKey ===
      'child'
  ) {
    const endpoint =
      resolveEndpoint(
        [
          input.connectorMemberId,
          input.tappedMemberId,
        ],
        true,
      );

    if ('missing' in endpoint) {
      return endpoint.missing
        ? missingEndpointResult(
            'PARENT_ENDPOINT_REQUIRED',
          )
        : blockedEndpointResult(
            endpoint.blocker,
          );
    }

    const parent =
      canonicalEndpoint(
        endpoint.member.id,
      );

    const blocker =
      validateParentShape(
        parent,
        subjectReference,
      );

    if (blocker) {
      return blockedEndpointResult(
        blocker,
      );
    }

    return ready([
      {
        factClass:
          'parent-child',

        source:
          parent,

        target:
          subjectReference,

        direction:
          'directed',

        createsAncestry:
          true,

        partnershipStatus:
          'not-applicable',

        evidenceMemberIds:
          evidenceForEndpoints([
            parent,
            subjectReference,
          ]),
      },
    ]);
  }

  if (
    input.optionKey ===
      'grandparent' ||
    input.optionKey ===
      'great-grandparent'
  ) {
    const endpoint =
      resolveEndpoint(
        [
          input.connectorMemberId,
          input.tappedMemberId,
        ],
        false,
      );

    if ('missing' in endpoint) {
      return endpoint.missing
        ? missingEndpointResult(
            'GENERATION_ANCHOR_REQUIRED',
            [
              'CANONICAL_GENERATION_ANCHOR',
            ],
          )
        : blockedEndpointResult(
            endpoint.blocker,
          );
    }

    const anchor =
      endpoint.member;

    const grandparentAnchorProven =
      (
        viewer.parentIds ??
        []
      ).includes(
        anchor.id,
      );

    const greatGrandparentAnchorProven =
      activeMembers.some(
        candidate =>
          (
            viewer.parentIds ??
            []
          ).includes(
            candidate.id,
          ) &&
          (
            candidate.parentIds ??
            []
          ).includes(
            anchor.id,
          ),
      );

    const topologyProven =
      input.optionKey ===
        'grandparent'
        ? grandparentAnchorProven
        : greatGrandparentAnchorProven;

    if (!topologyProven) {
      return blockedEndpointResult(
        'GENERATION_ANCHOR_TOPOLOGY_UNPROVEN',
      );
    }

    const anchorReference =
      canonicalEndpoint(
        anchor.id,
      );

    const blocker =
      validateParentShape(
        subjectReference,
        anchorReference,
      );

    if (blocker) {
      return blockedEndpointResult(
        blocker,
      );
    }

    return ready([
      {
        factClass:
          'parent-child',

        source:
          subjectReference,

        target:
          anchorReference,

        direction:
          'directed',

        createsAncestry:
          true,

        partnershipStatus:
          'not-applicable',

        evidenceMemberIds:
          evidenceForEndpoints([
            subjectReference,
            anchorReference,
          ]),
      },
    ]);
  }

  if (
    input.optionKey ===
      'sibling'
  ) {
    const endpoint =
      resolveEndpoint(
        [
          input.connectorMemberId,
          input.tappedMemberId,
        ],
        true,
      );

    if ('missing' in endpoint) {
      return endpoint.missing
        ? missingEndpointResult(
            'SIBLING_ENDPOINT_REQUIRED',
          )
        : blockedEndpointResult(
            endpoint.blocker,
          );
    }

    const anchorReference =
      canonicalEndpoint(
        endpoint.member.id,
      );

    if (
      subjectReference.kind ===
        'canonical-member'
    ) {
      const topology =
        planCanonicalTreeAncestryReconnection({
          members:
            activeMembers,

          subjectMemberId:
            subjectReference.memberId,

          intent:
            'CONNECT_EXPLICIT_SIBLINGS',

          anchorMemberId:
            endpoint.member.id,
        });

      if (!topology.authorized) {
        return blockedEndpointResult(
          topologyBlocker(
            topology.status,
          ),
        );
      }
    }

    /*
     * GREEN v1 never fabricates shared parent facts
     * merely from sibling intent. The explicit lateral
     * sibling fact is the bounded fallback.
     */
    return ready([
      {
        factClass:
          'sibling',

        source:
          subjectReference,

        target:
          anchorReference,

        direction:
          'symmetric',

        createsAncestry:
          false,

        partnershipStatus:
          'not-applicable',

        evidenceMemberIds:
          evidenceForEndpoints([
            subjectReference,
            anchorReference,
          ]),
      },
    ]);
  }

  if (
    input.optionKey ===
      'v_coparent'
  ) {
    const endpoint =
      resolveEndpoint(
        [
          input.connectorMemberId,
          input.tappedMemberId,
        ],
        false,
      );

    if ('missing' in endpoint) {
      return endpoint.missing
        ? missingEndpointResult(
            'SHARED_CHILD_ENDPOINT_REQUIRED',
            [
              'CANONICAL_SHARED_CHILD_ENDPOINT',
            ],
          )
        : blockedEndpointResult(
            endpoint.blocker,
          );
    }

    const sharedChild =
      endpoint.member;

    if (
      !(
        sharedChild.parentIds ??
        []
      ).includes(
        viewer.id,
      )
    ) {
      return blockedEndpointResult(
        'SHARED_CHILD_NOT_VIEWER_CHILD',
      );
    }

    const childReference =
      canonicalEndpoint(
        sharedChild.id,
      );

    const blocker =
      validateParentShape(
        subjectReference,
        childReference,
      );

    if (blocker) {
      return blockedEndpointResult(
        blocker,
      );
    }

    return ready(
      [
        {
          factClass:
            'parent-child',

          source:
            subjectReference,

          target:
            childReference,

          direction:
            'directed',

          createsAncestry:
            true,

          partnershipStatus:
            'not-applicable',

          evidenceMemberIds:
            evidenceForEndpoints([
              subjectReference,
              childReference,
              canonicalEndpoint(
                viewer.id,
              ),
            ]),
        },
      ],
      [
        'Co-Parent',
      ],
    );
  }

  if (
    input.optionKey ===
      'spouse'
  ) {
    const endpoint =
      resolveEndpoint(
        [
          input.connectorMemberId,
          input.tappedMemberId,
        ],
        false,
      );

    if ('missing' in endpoint) {
      return endpoint.missing
        ? missingEndpointResult(
            'PARTNER_ENDPOINT_REQUIRED',
          )
        : blockedEndpointResult(
            endpoint.blocker,
          );
    }

    const partnerReference =
      canonicalEndpoint(
        endpoint.member.id,
      );

    if (
      subjectReference.kind ===
        'canonical-member' &&
      subjectReference.memberId ===
        partnerReference.memberId
    ) {
      return blockedEndpointResult(
        'SELF_SPOUSE_FACT',
      );
    }

    return ready([
      {
        factClass:
          'spouse-partner',

        source:
          subjectReference,

        target:
          partnerReference,

        direction:
          'symmetric',

        createsAncestry:
          false,

        partnershipStatus:
          'current',

        evidenceMemberIds:
          evidenceForEndpoints([
            subjectReference,
            partnerReference,
          ]),
      },
    ]);
  }

  if (
    policy.disposition ===
      'REQUIRES_TWO_ENDPOINTS'
  ) {
    const first =
      resolveEndpoint(
        [
          input.connectorMemberId,
        ],
        false,
      );

    const second =
      resolveEndpoint(
        [
          input.secondaryConnectorMemberId,
        ],
        false,
      );

    if (
      'missing' in first ||
      'missing' in second
    ) {
      const blocked =
        (
          'missing' in first &&
          !first.missing
        )
          ? first
          : (
              'missing' in second &&
              !second.missing
            )
            ? second
            : null;

      if (blocked) {
        return blockedEndpointResult(
          blocked.blocker,
        );
      }

      return missingEndpointResult(
        'SPOUSE_PARENT_ENDPOINTS_REQUIRED',
        [
          'CANONICAL_SPOUSE_ENDPOINT',
          'CANONICAL_SPOUSE_PARENT_ENDPOINT',
        ],
      );
    }

    /*
     * Parent-in-law requires certified partnership and
     * spouse-parent edge evidence. GREEN v1 does not
     * reconstruct that truth from display state.
     */
    return blockedEndpointResult(
      'CANONICAL_EDGE_EVIDENCE_REQUIRED',
    );
  }

  /*
   * Stages 6–8 — unsupported/unproven options fail
   * closed; finalization remains deterministic.
   */
  return finalizePlannerResult({
    status:
      'BLOCKED',

    blockers:
      [
        'UNSUPPORTED_OR_UNPROVEN_AUTHORING_INTENT',
      ],

    subjectIdentityResolutionRequired,

    memberById,
  });
};
